import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  ArrowRight, Brain, Zap, Clock, Globe, Shield,
  TrendingUp, Code, Database, Cpu, Settings, CheckCircle2
} from 'lucide-react';

const LLMDevelopmentServices = () => {
  const services = [
    {
      icon: Settings,
      title: 'LLM Fine-tuning',
      description: 'Fine-tune GPT-4, Llama 3, and Mistral on your domain data for superior accuracy and 80% lower inference costs',
      deliverables: ['Training data preparation', 'LoRA / QLoRA fine-tuning', 'RLHF alignment', 'Model evaluation & benchmarking']
    },
    {
      icon: Database,
      title: 'RAG Systems',
      description: 'Connect any LLM to your private knowledge base for accurate, grounded responses with source citations',
      deliverables: ['Document ingestion pipelines', 'Chunking & embedding strategy', 'Hybrid search (BM25 + semantic)', 'Context management & reranking']
    },
    {
      icon: Brain,
      title: 'Private LLM Deployment',
      description: 'Deploy open-source models (Llama, Mistral, Phi) inside your own cloud — zero data leakage, full compliance',
      deliverables: ['AWS / Azure / GCP deployment', 'vLLM & TGI serving', 'Auto-scaling', 'SOC2 & HIPAA ready']
    },
    {
      icon: Cpu,
      title: 'LLM API Integration',
      description: 'Integrate GPT-4o, Claude, Gemini, or any LLM into your product with robust, production-grade wrappers',
      deliverables: ['Retry & fallback logic', 'Prompt versioning', 'Cost monitoring', 'Response caching']
    },
    {
      icon: Code,
      title: 'Prompt Engineering & Evals',
      description: 'Systematic prompt design and automated evaluation pipelines to maximise accuracy and minimise cost',
      deliverables: ['Prompt library development', 'Chain-of-thought & few-shot design', 'Automated eval suites', 'Regression testing']
    },
    {
      icon: TrendingUp,
      title: 'LLM-Powered Products',
      description: 'End-to-end AI product development — from idea to production SaaS powered by the latest LLMs',
      deliverables: ['Full-stack development', 'Multi-tenant architecture', 'Billing integration', 'Analytics & dashboards']
    }
  ];

  const caseStudies = [
    {
      industry: 'Legal Tech',
      challenge: 'Contract review taking senior lawyers 4–6 hours per document, limiting throughput',
      solution: 'Fine-tuned Llama 3 model trained on 10,000 contracts — highlights risk clauses and deviations from standard terms',
      result: 'Review time: 4 hours → 12 minutes. 98% clause detection accuracy.',
      timeline: '6 weeks',
      link: '/case-studies/insurance-automation'
    },
    {
      industry: 'Healthcare',
      challenge: 'Clinical notes unstructured and inconsistent, making data analysis impossible',
      solution: 'Custom RAG system with fine-tuned medical NLP model extracting structured data from free-text notes',
      result: '40% improvement in early risk detection. HIPAA compliant, deployed on private AWS.',
      timeline: '8 weeks',
      link: '/case-studies/healthcare-ai'
    },
    {
      industry: 'E-commerce',
      challenge: 'Product catalogue of 500K+ items with inconsistent, low-quality descriptions hurting conversion',
      solution: 'Batch LLM pipeline generating SEO-optimised descriptions at scale, with brand voice fine-tuning',
      result: '10x faster catalogue updates. 18% increase in organic product page traffic.',
      timeline: '4 weeks',
      link: '/case-studies/retail-analytics'
    }
  ];

  const models = [
    { name: 'GPT-4o / GPT-4 Turbo', provider: 'OpenAI', best: 'Complex reasoning, tool calling, vision' },
    { name: 'Claude 3.5 Sonnet / Opus', provider: 'Anthropic', best: 'Long context, analysis, safety-critical apps' },
    { name: 'Gemini Pro / Ultra', provider: 'Google', best: 'Multimodal, large context windows' },
    { name: 'Llama 3.1 (8B / 70B / 405B)', provider: 'Meta (Open Source)', best: 'Private deployment, fine-tuning, cost-sensitive' },
    { name: 'Mistral / Mixtral', provider: 'Mistral AI (Open Source)', best: 'Fast inference, European data residency' },
    { name: 'Phi-3 / Qwen', provider: 'Microsoft / Alibaba', best: 'Edge deployment, low-resource environments' }
  ];

  const process = [
    { step: '01', title: 'Model Selection & Architecture', description: 'Choose the right model, hosting strategy, and architecture based on your accuracy, cost, and compliance requirements', duration: '3–5 days' },
    { step: '02', title: 'Data Preparation', description: 'Clean, format, and augment your training or retrieval data — the most critical step for performance', duration: '1–2 weeks' },
    { step: '03', title: 'Build & Train', description: 'Fine-tuning, RAG pipeline construction, or product integration — with continuous evaluation throughout', duration: '2–5 weeks' },
    { step: '04', title: 'Deploy & Monitor', description: 'Production deployment with latency monitoring, cost tracking, and model drift detection', duration: '1 week' }
  ];

  return (
    <>
      <Helmet>
        <title>LLM Development Services | Fine-tuning, RAG & Private LLM Deployment | Agility</title>
        <meta name="description" content="Expert LLM development services: fine-tuning, RAG systems, private LLM deployment, and LLM-powered product development. GPT-4, Claude, Llama 3, Mistral. Delivered in 4-8 weeks by senior AI engineers." />
        <meta name="keywords" content="LLM development services, custom LLM development, LLM fine-tuning services, RAG development, large language model development, private LLM deployment, LLM integration services, LLM consulting, generative AI development, LLM application development" />
        <link rel="canonical" href="https://www.agilitytech.ai/llm-development-services" />
        <meta property="og:title" content="LLM Development Services | Fine-tuning, RAG & Private Deployment | Agility" />
        <meta property="og:description" content="Fine-tuning, RAG, private LLM deployment, and LLM product development. GPT-4o, Claude, Llama 3. 4-8 week delivery." />
        <meta property="og:url" content="https://www.agilitytech.ai/llm-development-services" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.agilitytech.ai/agility hex fonts.png" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "LLM Development Services",
            "description": "Custom LLM development including fine-tuning, RAG systems, private deployment, and end-to-end LLM-powered product development.",
            "provider": { "@type": "Organization", "name": "Agility", "url": "https://www.agilitytech.ai" },
            "areaServed": "Worldwide",
            "serviceType": "LLM Development"
          })}
        </script>
      </Helmet>

      {/* Hero */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden bg-gradient-to-br from-blue-900 via-black to-teal-900">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0 bg-[linear-gradient(30deg,#14b8a6_12%,transparent_12.5%,transparent_87%,#14b8a6_87.5%,#14b8a6),linear-gradient(150deg,#14b8a6_12%,transparent_12.5%,transparent_87%,#14b8a6_87.5%,#14b8a6)]" style={{ backgroundSize: '80px 140px' }} />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-500/10 border border-teal-500/30 rounded-full mb-8">
            <Brain className="w-4 h-4 text-teal-400" />
            <span className="text-teal-300 text-sm font-medium">LLM Development</span>
          </div>
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
            LLMs Built for <span className="text-teal-400">Your Business</span>
          </h1>
          <p className="text-xl text-gray-300 leading-relaxed mb-8 max-w-3xl mx-auto">
            Fine-tuning, RAG, private deployment, LLM-powered products — end-to-end LLM engineering
            on GPT-4o, Claude 3.5, Llama 3, and Mistral. Delivered in 4–8 weeks.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Link to="/contact" className="group inline-flex items-center justify-center gap-2 bg-teal-500 hover:bg-teal-600 text-white px-8 py-4 rounded-lg font-semibold transition-all">
              Start Your LLM Project <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link to="/generative-ai-consulting" className="group inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white px-8 py-4 rounded-lg font-semibold backdrop-blur-sm border border-white/20 transition-all">
              Generative AI Consulting <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto">
            {[['4–8', 'Weeks to Production'],['80%', 'Cost Reduction via Fine-tuning'],['6+', 'LLM Families Supported'],['200+', 'AI Projects Delivered']].map(([stat, label]) => (
              <div key={label} className="text-center">
                <div className="text-4xl font-bold text-teal-400 mb-2">{stat}</div>
                <div className="text-gray-400">{label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Full-Spectrum <span className="text-teal-600">LLM Services</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">Everything from model selection to production deployment — under one roof</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, i) => {
              const Icon = service.icon;
              return (
                <div key={i} className="fade-in-up bg-gradient-to-br from-gray-50 to-white rounded-xl p-8 border-2 border-gray-200 hover:border-teal-500 transition-all">
                  <Icon className="w-12 h-12 text-teal-600 mb-6" />
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{service.title}</h3>
                  <p className="text-gray-600 mb-6">{service.description}</p>
                  <div className="flex flex-wrap gap-2">
                    {service.deliverables.map((d, j) => (
                      <span key={j} className="px-3 py-1 bg-teal-50 text-teal-700 rounded-full text-sm font-medium">{d}</span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Models */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Every Major <span className="text-teal-600">LLM Supported</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">We're model-agnostic — we pick the best LLM for your use case, budget, and compliance requirements</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {models.map((model, i) => (
              <div key={i} className="fade-in-up bg-white rounded-xl p-6 border border-gray-200 hover:border-teal-400 transition-all">
                <div className="text-xs font-semibold text-teal-600 mb-2">{model.provider}</div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">{model.name}</h3>
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-teal-500 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-gray-600">{model.best}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              LLM Projects <span className="text-teal-600">We've Shipped</span>
            </h2>
          </div>
          <div className="grid lg:grid-cols-3 gap-8">
            {caseStudies.map((study, i) => (
              <Link key={i} to={study.link} className="fade-in-up bg-gradient-to-br from-teal-50 to-white rounded-xl p-8 border border-teal-200 hover:border-teal-400 hover:shadow-lg transition-all group">
                <div className="text-sm font-semibold text-teal-600 mb-4">{study.industry}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Challenge</h3>
                <p className="text-gray-700 mb-4">{study.challenge}</p>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Solution</h3>
                <p className="text-gray-700 mb-4">{study.solution}</p>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Results</h3>
                <p className="text-gray-700 mb-6">{study.result}</p>
                <div className="flex items-center justify-between pt-6 border-t border-teal-200">
                  <div>
                    <div className="text-sm text-gray-500">Timeline</div>
                    <div className="font-semibold text-gray-900">{study.timeline} to production</div>
                  </div>
                  <div className="flex items-center gap-1 text-teal-600 font-semibold text-sm group-hover:translate-x-1 transition-transform">
                    Read more <ArrowRight className="w-4 h-4" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-24 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              From Data to <span className="text-teal-400">Production LLM</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {process.map((p, i) => (
              <div key={i} className="fade-in-up text-center">
                <div className="w-20 h-20 bg-teal-500 text-white rounded-full flex items-center justify-center text-3xl font-bold mx-auto mb-6">{p.step}</div>
                <h3 className="text-xl font-bold text-white mb-3">{p.title}</h3>
                <p className="text-gray-300 mb-4">{p.description}</p>
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-teal-900/50 text-teal-300 rounded-full text-sm font-medium border border-teal-700">
                  <Clock className="w-4 h-4" /> {p.duration}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-gradient-to-br from-teal-600 to-teal-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Ready to Build with LLMs?</h2>
          <p className="text-xl text-teal-100 mb-8 max-w-2xl mx-auto">
            From a single-model integration to a full fine-tuned deployment — we scope it, build it, and ship it.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="group inline-flex items-center justify-center gap-2 bg-white text-teal-700 hover:bg-teal-50 px-8 py-4 rounded-lg font-bold text-lg transition-all">
              Discuss Your LLM Project <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link to="/openai-gpt-development" className="inline-flex items-center justify-center gap-2 border-2 border-white text-white hover:bg-white/10 px-8 py-4 rounded-lg font-semibold text-lg transition-all">
              OpenAI / GPT Development
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default LLMDevelopmentServices;
